import React from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import BaseQuestion from '../base-question'
import { getCustomComponent } from './custom-components'

interface CustomQuestionProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    component?: string
  }
  // RHF mode
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: any
  skipValue?: boolean
  error?: string
  onChange?: (value: any) => void
  onSkipChange?: (skipValue: boolean) => void
  // Shared props
  clientId?: number
  responseByQuestionId?: Record<number, any[]>
  allQuestions?: Array<{ id: number | string }>
}

const CustomQuestion: React.FC<CustomQuestionProps> = ({
  question,
  control,
  errors,
  value,
  skipValue,
  error,
  onChange,
  onSkipChange,
  clientId,
  responseByQuestionId,
  allQuestions,
}) => {
  const isRHFMode = !!control && !!errors
  const CustomComponent = getCustomComponent(question.component)

  const questionId =
    typeof question.id === 'string' ? parseInt(question.id, 10) : question.id

  if (!CustomComponent) {
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        control={isRHFMode ? control : undefined}
        errors={isRHFMode ? errors : undefined}
        error={!isRHFMode ? error : undefined}>
        <div className="error">
          Unknown custom component: {question.component}
        </div>
      </BaseQuestion>
    )
  }

  if (isRHFMode) {
    // RHF mode
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        control={control}
        errors={errors}>
        <Controller
          name={`question_${question.id}`}
          control={control}
          rules={{
            required: question.isRequired ? 'This field is required' : false,
          }}
          render={({ field }) => (
            <CustomComponent
              question={question}
              field={field}
              control={control}
              errors={errors}
              clientId={clientId}
              responseByQuestionId={responseByQuestionId}
              allQuestions={allQuestions}
            />
          )}
        />
      </BaseQuestion>
    )
  } else {
    // Zustand mode
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        error={error}>
        <CustomComponent
          question={question}
          value={value}
          onChange={onChange}
          error={error}
          skipValue={skipValue}
          onSkipChange={onSkipChange}
          clientId={clientId}
          responseByQuestionId={responseByQuestionId}
          allQuestions={allQuestions}
        />
      </BaseQuestion>
    )
  }
}

export default CustomQuestion
